import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useSite } from '@/context/SiteContext';
import { MAIN_NAV } from '@/constants/nav';
import { getIcon } from '@/lib/icons';
import { Menu, Heart, Handshake, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const Header = () => {
    const { settings } = useSite();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const socials = settings?.social_links || [];

    return (
        <header
            className={cn(
                'sticky top-0 z-40 w-full border-b border-border bg-background',
                scrolled ? 'shadow-[0_6px_20px_rgba(15,23,42,0.06)]' : ''
            )}
            data-testid="site-header"
        >
            {/* Top strip */}
            <div className="hidden md:block border-b border-border bg-[hsl(var(--primary))] text-primary-foreground">
                <div className="container-wide flex h-9 items-center justify-between text-xs">
                    <div className="flex items-center gap-4">
                        {settings?.contact_phone && (
                            <a
                                href={`tel:${settings.contact_phone}`}
                                className="hover:underline"
                                data-testid="header-phone"
                            >
                                {settings.contact_phone}
                            </a>
                        )}
                        {settings?.contact_email && (
                            <a
                                href={`mailto:${settings.contact_email}`}
                                className="hover:underline"
                                data-testid="header-email"
                            >
                                {settings.contact_email}
                            </a>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {socials.slice(0, 5).map((s) => {
                            const Icon = getIcon(s.platform);
                            return (
                                <a
                                    key={s.platform}
                                    href={s.url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="opacity-90 hover:opacity-100"
                                    aria-label={s.platform}
                                    data-testid={`header-social-${s.platform}`}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                </a>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Main bar */}
            <div className={cn('container-wide flex items-center gap-4', scrolled ? 'h-16' : 'h-20')}>
                <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
                    {settings?.logo_url ? (
                        <img
                            src={settings.logo_url}
                            alt="VVVSI Trust"
                            className={cn(
                                'w-auto object-contain transition-[height] duration-200',
                                scrolled ? 'h-12 sm:h-14' : 'h-14 sm:h-16'
                            )}
                        />
                    ) : (
                        <div className="h-12 w-12 rounded-md bg-[hsl(var(--primary))] text-primary-foreground flex items-center justify-center font-display font-bold">
                            V
                        </div>
                    )}
                    <div className="leading-tight hidden sm:block">
                        <div
                            className={cn(
                                'font-display font-bold text-[hsl(var(--primary))] tracking-tight transition-[font-size] duration-200',
                                scrolled ? 'text-xl' : 'text-2xl'
                            )}
                            data-testid="header-brand-name"
                        >
                            VVVSI
                        </div>
                        <div
                            className="text-[11px] sm:text-xs text-muted-foreground font-medium max-w-[260px] truncate"
                            data-testid="header-brand-caption"
                        >
                            Vallalar Velai Vaaipu Sevai Iyakkam
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1 ml-auto" data-testid="header-nav">
                    {MAIN_NAV.slice(0, 5).map((item) => {
                        // Item with a submenu → render as dropdown trigger.
                        if (item.children && item.children.length > 0) {
                            const isActive =
                                location.pathname === item.to ||
                                item.children.some((c) => location.pathname === c.to);
                            return (
                                <DropdownMenu key={item.to}>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            className={cn(
                                                'px-3 py-2 text-sm font-medium rounded-md flex items-center gap-1 transition-colors',
                                                isActive
                                                    ? 'text-[hsl(var(--primary))] bg-[hsl(var(--secondary))]'
                                                    : 'text-foreground/80 hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))]/60'
                                            )}
                                            data-testid={`nav-${item.to.replace(/\//g, '')}-trigger`}
                                            type="button"
                                        >
                                            {item.label}
                                            <ChevronDown className="h-3.5 w-3.5" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start" sideOffset={6} className="min-w-[280px]">
                                        {item.children.map((child) => (
                                            <DropdownMenuItem
                                                key={child.to}
                                                asChild
                                                className="cursor-pointer"
                                            >
                                                <NavLink
                                                    to={child.to}
                                                    className={({ isActive: childActive }) =>
                                                        cn(
                                                            'w-full px-2 py-1.5 text-sm rounded-sm',
                                                            childActive
                                                                ? 'text-[hsl(var(--primary))] font-medium'
                                                                : 'text-foreground/85'
                                                        )
                                                    }
                                                    data-testid={`nav-sub-${child.to.replace(/\//g, '-').replace(/^-/, '')}`}
                                                >
                                                    {child.label}
                                                </NavLink>
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            );
                        }
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === '/'}
                                className={({ isActive }) =>
                                    cn(
                                        'px-3 py-2 text-sm font-medium rounded-md transition-colors',
                                        isActive
                                            ? 'text-[hsl(var(--primary))] bg-[hsl(var(--secondary))]'
                                            : 'text-foreground/80 hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))]/60'
                                    )
                                }
                                data-testid={`nav-${item.to.replace(/\//g, '')}`}
                            >
                                {item.label}
                            </NavLink>
                        );
                    })}
                    {MAIN_NAV.length > 5 && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className={cn(
                                        'px-3 py-2 text-sm font-medium rounded-md flex items-center gap-1 transition-colors',
                                        MAIN_NAV.slice(5).some((n) => location.pathname === n.to)
                                            ? 'text-[hsl(var(--primary))] bg-[hsl(var(--secondary))]'
                                            : 'text-foreground/80 hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))]/60'
                                    )}
                                    data-testid="nav-more-trigger"
                                    type="button"
                                >
                                    More <ChevronDown className="h-3.5 w-3.5" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" sideOffset={6} className="min-w-[240px]">
                                {MAIN_NAV.slice(5).map((item) => (
                                    <DropdownMenuItem
                                        key={item.to}
                                        asChild
                                        className="cursor-pointer"
                                    >
                                        <NavLink
                                            to={item.to}
                                            className={({ isActive }) =>
                                                cn(
                                                    'w-full px-2 py-1.5 text-sm rounded-sm',
                                                    isActive
                                                        ? 'text-[hsl(var(--primary))] font-medium'
                                                        : 'text-foreground/85'
                                                )
                                            }
                                            data-testid={`nav-more-${item.to.replace(/\//g, '')}`}
                                        >
                                            {item.label}
                                        </NavLink>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </nav>

                <div className="ml-auto lg:ml-0 flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/volunteer')}
                        className="hidden sm:inline-flex border-[hsl(var(--primary))]/25 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5"
                        data-testid="header-volunteer-button"
                    >
                        <Handshake className="h-4 w-4 mr-1.5" />
                        Volunteer
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => navigate('/donate')}
                        className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold"
                        data-testid="header-donate-button"
                    >
                        <Heart className="h-4 w-4 mr-1.5" />
                        Donate Now
                    </Button>

                    {/* Mobile menu */}
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="lg:hidden"
                                data-testid="header-mobile-menu-button"
                                aria-label="Open menu"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[300px] sm:w-[380px] p-0">
                            <SheetTitle className="sr-only">Navigation</SheetTitle>
                            <div className="p-6 border-b border-border flex items-center gap-3">
                                {settings?.logo_url && (
                                    <img
                                        src={settings.logo_url}
                                        alt="VVVSI"
                                        className="h-10 w-10 object-contain"
                                    />
                                )}
                                <div className="font-display font-semibold text-[hsl(var(--primary))]">
                                    VVVSI
                                </div>
                            </div>
                            <div className="p-3 flex flex-col">
                                {MAIN_NAV.map((item) => {
                                    if (item.children && item.children.length > 0) {
                                        return (
                                            <div key={item.to} className="mb-1">
                                                <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--accent))]">
                                                    {item.label}
                                                </div>
                                                {item.children.map((c) => (
                                                    <NavLink
                                                        key={c.to}
                                                        to={c.to}
                                                        className={({ isActive }) =>
                                                            cn(
                                                                'block px-4 py-2.5 rounded-md text-sm',
                                                                isActive
                                                                    ? 'text-[hsl(var(--primary))] bg-[hsl(var(--secondary))] font-medium'
                                                                    : 'text-foreground/80 hover:bg-[hsl(var(--secondary))]/60'
                                                            )
                                                        }
                                                        data-testid={`mobile-nav-sub-${c.to.replace(/\//g, '-').replace(/^-/, '')}`}
                                                    >
                                                        {c.label}
                                                    </NavLink>
                                                ))}
                                            </div>
                                        );
                                    }
                                    return (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            end={item.to === '/'}
                                            className={({ isActive }) =>
                                                cn(
                                                    'px-4 py-3 rounded-md text-sm font-medium',
                                                    isActive
                                                        ? 'text-[hsl(var(--primary))] bg-[hsl(var(--secondary))]'
                                                        : 'text-foreground/80 hover:bg-[hsl(var(--secondary))]/60'
                                                )
                                            }
                                            data-testid={`mobile-nav-${item.to.replace(/\//g, '')}`}
                                        >
                                            {item.label}
                                        </NavLink>
                                    );
                                })}
                            </div>
                            <div className="p-4 border-t border-border flex flex-col gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => navigate('/volunteer')}
                                    className="border-[hsl(var(--primary))]/25 text-[hsl(var(--primary))]"
                                    data-testid="mobile-volunteer-button"
                                >
                                    <Handshake className="h-4 w-4 mr-1.5" />
                                    Volunteer
                                </Button>
                                <Button
                                    onClick={() => navigate('/donate')}
                                    className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold"
                                    data-testid="mobile-donate-button"
                                >
                                    <Heart className="h-4 w-4 mr-1.5" />
                                    Donate Now
                                </Button>
                                {socials.length > 0 && (
                                    <div className="flex items-center gap-3 pt-3">
                                        {socials.map((s) => {
                                            const Icon = getIcon(s.platform);
                                            return (
                                                <a
                                                    key={s.platform}
                                                    href={s.url}
                                                    target="_blank"
                                                    rel="noreferrer noopener"
                                                    className="text-[hsl(var(--primary))]"
                                                    aria-label={s.platform}
                                                >
                                                    <Icon className="h-5 w-5" />
                                                </a>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default Header;
